/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import { selectCommandRun, selectCurrencyList, selectUrlTab } from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { consoleCommands } from '../utils/constants';
import useCommandPipeline from './use-command-pipeline';

/**
 * Custom hook to execute the command flow of a dashboard tab.
 * Runs the steps of the tab once the stay date command is applied by the dashboard header.
 * @param {Array} steps - ordered step list of the tab, see useCommandPipeline
 * @param {Object} context - current state the steps read
 * @param {Number} tabValue - tab value of the filter panel
 * @param {Array} tabValues - tab values the steps belong to
 */
const useTabCommandPipeline = (steps, context, tabValue, tabValues) => {
  const dispatch = useDispatch();
  //
  const urlTab = useSelector(selectUrlTab);
  const commandRun = useSelector(selectCommandRun);
  const authUser = useSelector(selectAuthUser);
  const organizationId = useSelector(selectOrganizationId);
  const currencyList = useSelector(selectCurrencyList);
  // stay date is applied by the dashboard header before the tab filters
  const isStayDateApplied =
    !commandRun?.keys?.includes(consoleCommands.STAY_DATE) ||
    Boolean(commandRun?.isStayDateApplied);
  //
  useCommandPipeline(
    steps,
    { ...context, dispatch, authUser, organizationId, currencyList },
    {
      enabled: tabValues.includes(tabValue) && tabValue === urlTab && isStayDateApplied,
      // command url is applied, tab commands are not forwarded any more
      onDone: () => dispatch(dashboardActions.setUrlTab(null)),
    }
  );
};

export default useTabCommandPipeline;
